import { mustParse } from './geometry.ts';
import { MapValidationError, validateMap } from './map.ts';
import { fleetSize, makeRuleset } from './ruleset.ts';
import { Rng, seedRng } from './rng.ts';
import { Draft } from './draft.ts';
import {
  SHIP_CLASS_IDS,
  SIDES,
  type FortState,
  type GameEvent,
  type GameState,
  type MapDef,
  type Player,
  type Ruleset,
  type ShipClassId,
  type ShipState,
  type Side,
} from './types.ts';
import { endBlock, entryBlock } from './world.ts';

export const RULES_VERSION = '1.1.0';

/** A ship placed explicitly instead of by random setup (scenarios, tests, sandbox puzzles). */
export interface PlacedShip {
  readonly side: Side;
  readonly cls: ShipClassId;
  readonly cell: string;
  readonly id?: string;
  readonly damage?: number;
}

export interface CreateGameOptions {
  /**
   * Fixed placement. When given, R-SETUP-* is skipped and the map need not satisfy the
   * per-side composition of R-MAP-07.
   */
  readonly ships?: readonly PlacedShip[];
  /** Fort hits at start, by fort id (scenarios only). */
  readonly fortHits?: Readonly<Record<string, number>>;
}

function newShip(id: string, side: Side, cls: ShipClassId, cell: string): ShipState {
  return {
    id,
    side,
    cls,
    cell,
    damage: 0,
    sunk: false,
    shotsLeft: 0,
    moved: false,
    inGroup: false,
    fired: false,
    firedInPort: false,
    dockedOnTurn: null,
    repairingFort: null,
  };
}

/** R-SETUP-01..03 */
function randomSetup(map: MapDef, ruleset: Ruleset, rng: Rng): ShipState[] {
  const { deployment } = ruleset;
  const total = fleetSize(ruleset.fleet);
  const ships: ShipState[] = [];
  for (const side of SIDES) {
    const firsts = map.forts.filter((f) => f.side === side && f.class === 'first_line');
    const seconds = map.forts.filter((f) => f.side === side && f.class === 'second_line');
    const quota =
      firsts.length * deployment.firstLine +
      (seconds.length - 1) * deployment.secondLine +
      deployment.largePort;
    if (total !== quota) {
      throw new RangeError(`Fleet has ${total} ships but setup quotas place ${quota}`);
    }
    // Class order, then the PRNG shuffle: kept as it was so old games replay unchanged (R-RNG-02).
    const fleet: ShipClassId[] = SHIP_CLASS_IDS.flatMap((c) =>
      Array.from({ length: ruleset.fleet[c] ?? 0 }, () => c),
    );
    const counters: Partial<Record<ShipClassId, number>> = {};
    const ids = fleet.map((c) => {
      counters[c] = (counters[c] ?? 0) + 1;
      return { id: `${side}-${c}-${counters[c]}`, cls: c };
    });
    const shuffled = rng.shuffle(ids);
    let large: string;
    if (ruleset.largePortAssignment === 'random') {
      large = (seconds[rng.int(seconds.length)] as (typeof seconds)[number]).id;
    } else {
      large = ruleset.largePortAssignment[side];
      if (!seconds.some((f) => f.id === large)) {
        throw new RangeError(`largePortAssignment: ${large} is not a second-line fort of ${side}`);
      }
    }
    const plan = [
      ...firsts.map((f) => ({ fort: f, n: deployment.firstLine })),
      ...seconds.map((f) => ({ fort: f, n: f.id === large ? deployment.largePort : deployment.secondLine })),
    ];
    let next = 0;
    for (const { fort, n } of plan) {
      const slots = rng.shuffle(fort.portCells.flatMap((c) => [c, c]));
      for (let i = 0; i < n; i++) {
        const entry = shuffled[next++] as { id: string; cls: ShipClassId };
        ships.push(newShip(entry.id, side, entry.cls, slots[i] as string));
      }
    }
  }
  return ships;
}

/**
 * Creates a new game (R-SETUP-*, R-TURN-01). Throws `MapValidationError` for an invalid map
 * and `RangeError` for inconsistent options.
 */
export function createGame(
  map: MapDef,
  ruleset: Partial<Ruleset>,
  seed: number,
  players: Readonly<Record<Side, Player>>,
  options: CreateGameOptions = {},
): GameState {
  return createGameWithEvents(map, ruleset, seed, players, options).state;
}

export function createGameWithEvents(
  map: MapDef,
  rulesetOverrides: Partial<Ruleset>,
  seed: number,
  players: Readonly<Record<Side, Player>>,
  options: CreateGameOptions = {},
): { state: GameState; events: GameEvent[] } {
  const ruleset = makeRuleset(rulesetOverrides);
  const issues = validateMap(map, {
    requireComposition: options.ships === undefined,
    deployment: ruleset.deployment,
  });
  if (issues.length > 0) throw new MapValidationError(issues);
  const rng = new Rng(seedRng(seed));

  const forts: FortState[] = map.forts.map((f) => ({
    id: f.id,
    cls: f.class,
    side: f.side,
    cell: f.cell,
    portCells: f.portCells,
    hits: options.fortHits?.[f.id] ?? 0,
    destroyed: false,
    shotsLeft: 0,
    fired: false,
    repair: null,
  }));
  for (const fort of forts) {
    if (fort.hits >= ruleset.fortClasses[fort.cls].destroyedAt) {
      (fort as { destroyed: boolean }).destroyed = true;
    }
  }

  let ships: ShipState[];
  if (options.ships) {
    const counters = new Map<string, number>();
    ships = options.ships.map((p) => {
      const key = `${p.side}-${p.cls}`;
      counters.set(key, (counters.get(key) ?? 0) + 1);
      const ship = newShip(p.id ?? `${key}-${counters.get(key)}`, p.side, p.cls, p.cell);
      return { ...ship, damage: p.damage ?? 0 };
    });
    if (new Set(ships.map((s) => s.id)).size !== ships.length) {
      throw new RangeError('Duplicate ship ids in placement');
    }
  } else {
    ships = randomSetup(map, ruleset, rng);
  }

  const firstSide: Side = ruleset.firstSide === 'lot' ? (SIDES[rng.int(2)] as Side) : ruleset.firstSide;

  const base: GameState = {
    rulesVersion: RULES_VERSION,
    map,
    ruleset,
    seed,
    players,
    rng: rng.state,
    ships,
    forts,
    turn: { number: 0, side: firstSide, phase: 'open', group: [], spin: null, royalAtStart: [] },
    holdCount: { red: 0, blue: 0 },
    drawOffer: null,
    status: { kind: 'playing' },
    actionCount: 0,
  };

  if (options.ships) {
    // Placement must itself be legal: enterable cells within capacity.
    const placed: ShipState[] = [];
    for (const ship of ships) {
      const world = { ...base, ships: placed };
      const p = mustParse(ship.cell);
      const blocked = entryBlock(world, ship, p) ?? endBlock(world, ship, p);
      if (blocked) throw new RangeError(`Cannot place ${ship.id} at ${ship.cell} (${blocked.rule})`);
      placed.push(ship);
    }
  }

  const draft = new Draft(base);
  draft.startTurn(1, firstSide);
  const state = { ...draft.finish(), actionCount: 0 };
  return { state, events: draft.events };
}
